/**
 * =============================================================================
 * IDS CRM Web - CustomerSearchSelect
 * =============================================================================
 * SearchSelect preset for customer/distributor pickers. kind='customer' means
 * any non-distributor row (prospects included); kind='distributor' filters
 * server-side to distributors only; kind='any' applies no type filter.
 * typeFilter overrides all of these with an exact customer_type match.
 */

import React, { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import SearchSelect from './SearchSelect';

function CustomerSearchSelect({
  id,
  value,
  initialLabel,
  onSelect,
  kind = 'customer',
  statusFilter = null,
  typeFilter = null,
  distributorId = null,
  excludeIds = [],
  placeholder,
  disabled = false,
  clearable = false,
  selectedIndicator,
  minChars,
  getOptionSub
}) {
  const { authFetch } = useAuth();
  const excludeKey = excludeIds.filter(Boolean).join(',');

  const fetchOptions = useCallback(async (term) => {
    const params = new URLSearchParams({
      search: term,
      limit: '15',
      sort: 'business_name',
      order: 'asc'
    });
    if (typeFilter) {
      params.set('type', typeFilter);
    } else if (kind === 'distributor') {
      params.set('type', 'distributor');
    } else if (kind !== 'any') {
      params.set('exclude_type', 'distributor');
    }
    if (statusFilter) params.set('status', statusFilter);
    // Distributor-first flows: only that distributor's customers are pickable.
    if (distributorId) params.set('distributor_id', distributorId);

    const response = await authFetch(`/api/v1/customers?${params.toString()}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Customer search failed');
    const excluded = excludeKey ? excludeKey.split(',') : [];
    return (data.data || []).filter(row => !excluded.includes(row.customer_id));
  }, [authFetch, kind, statusFilter, typeFilter, distributorId, excludeKey]);

  const defaultIndicator = kind === 'distributor' ? 'Distributor selected' : 'Customer selected';

  return (
    <SearchSelect
      id={id}
      value={value}
      initialLabel={initialLabel}
      placeholder={placeholder || (kind === 'distributor' ? 'Search for a distributor...' : 'Search for a customer...')}
      disabled={disabled}
      fetchOptions={fetchOptions}
      getOptionId={(row) => row.customer_id}
      getOptionLabel={(row) => row.business_name}
      getOptionSub={getOptionSub || ((row) => row.customer_number)}
      onSelect={onSelect}
      clearable={clearable}
      selectedIndicator={selectedIndicator === undefined ? defaultIndicator : selectedIndicator}
      {...(minChars ? { minChars } : {})}
    />
  );
}

export default CustomerSearchSelect;
